import React, { useContext, useEffect, useMemo, useState } from "react";
import './NotificationDropdown.css';
import { CheckCheck, Loader2, BellOff, Trash2, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";
import { LanguageContext } from "../../constant/LanguageContext";
import NotificationItem from "./NotificationItem";
import { useAuth } from "../../context/AuthContext";
import { useProfile } from "../../hooks/useProfile";
import { resolveNotificationNavigation, applyViewSwitch } from "../../utils/notificationNavigation";
import { sessionManager } from "../../utils/sessionManager";
import { parseActionUrl } from "../../constant/notificationRoutes";
import { downloadPayslipPdf } from "../../pages/(User)salary_Final/utils/downloadPayslip";

// Relative-time labels ("5 min. ago") are recomputed on this cadence while
// the panel is open, so they never freeze at the value from when it opened.
const CLOCK_TICK_MS = 30000;

// Bucket a notification by calendar day so the list reads as Today /
// Yesterday / Earlier instead of one undifferentiated stream.
const dayBucket = (createdAt) => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "earlier";
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  if (date >= startOfToday) return "today";
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (date >= startOfYesterday) return "yesterday";
  return "earlier";
};

const NotificationDropdown = ({ isOpen, onClose, isMobile, onOpenRemotePunchAlert }) => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    hasLoaded,
    pagination,
    fetchNotifications,
    markAllAsRead,
    deleteAllNotifications,
  } = useNotifications();
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, permissions } = useAuth() || {};
  const { profile } = useProfile() || {};
  const [filter, setFilter] = useState("all");
  // "Salary paid" click: payslip PDF being generated before navigating.
  const [payslipBusy, setPayslipBusy] = useState(false);
  const [, setClock] = useState(0);

  const isAr = language === "ar";
  const t = {
    notifications: isAr ? "الإشعارات" : "Notifications",
    markAllRead: isAr ? "تحديد الكل كمقروء" : "Mark all as read",
    deleteAll: isAr ? "مسح الكل" : "Delete all",
    confirmDeleteAll: isAr ? "هل أنت متأكد من مسح جميع الإشعارات؟" : "Are you sure you want to delete all notifications?",
    close: isAr ? "إغلاق" : "Close",
    all: isAr ? "الكل" : "All",
    unread: isAr ? "غير المقروءة" : "Unread",
    today: isAr ? "اليوم" : "Today",
    yesterday: isAr ? "أمس" : "Yesterday",
    earlier: isAr ? "أقدم" : "Earlier",
    noNotifications: isAr ? "لا توجد إشعارات" : "No notifications yet",
    emptyHint: isAr ? "ستظهر الإشعارات الجديدة هنا فور وصولها." : "New notifications will appear here as they arrive.",
    noUnread: isAr ? "لا توجد إشعارات غير مقروءة" : "You're all caught up",
    noUnreadHint: isAr ? "لقد قرأت كل الإشعارات." : "You've read every notification.",
    loading: isAr ? "جاري التحميل..." : "Loading...",
    loadMore: isAr ? "تحميل المزيد" : "Load more",
    downloadingPayslip: isAr ? "جاري تحميل كشف المرتب (PDF)..." : "Downloading your payslip (PDF)...",
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setInterval(() => setClock((c) => c + 1), CLOCK_TICK_MS);
    return () => clearInterval(id);
  }, [isOpen]);

  // Every open starts on "All".
  useEffect(() => {
    if (isOpen) setFilter("all");
  }, [isOpen]);

  const visible = useMemo(
    () => (filter === "unread" ? notifications.filter((n) => !n.read) : notifications),
    [notifications, filter]
  );

  const groups = useMemo(() => {
    const buckets = { today: [], yesterday: [], earlier: [] };
    visible.forEach((n) => buckets[dayBucket(n.createdAt)].push(n));
    return ["today", "yesterday", "earlier"]
      .filter((key) => buckets[key].length)
      .map((key) => ({ key, items: buckets[key] }));
  }, [visible]);

  const handleNotificationClick = async (notification) => {
    if (payslipBusy) return;
    // Remote Punch alert (the company's alert recipients): opens its details over the current
    // page instead of navigating anywhere.
    if (notification?.type === "REMOTE_PUNCH_ALERT") {
      const alertId = parseActionUrl(notification?.actionUrl).remotePunchAlertId;
      if (alertId) onOpenRemotePunchAlert?.(alertId);
      onClose();
      return;
    }
    // Resolve the destination — the VIEW that owns the notification's page
    // for this account, the shell path, and the query params that let the
    // page focus the exact record (open the leave/complaint/document modal,
    // highlight the row, open the chat, ...). Marking as read already
    // happened in NotificationItem's own click handler.
    // Same precedence PrivateRoute uses to authorize routes (sessionManager
    // first, falling back to the AuthContext user object) — previously this
    // read `user?.role` first, a different order that could in theory pick a
    // stale role and resolve a destination PrivateRoute would then reject.
    const role = String(sessionManager.getUserRole() || user?.role || "user").toLowerCase();
    const target = resolveNotificationNavigation(notification, {
      role,
      permissions,
      pathname: location.pathname,
      selfCode: profile?.infinityCode || localStorage.getItem("infinityCode") || null,
    });
    let path = target?.path;

    // "Salary paid": download the paid month's payslip PDF FIRST, then open
    // the salary page on that month. If the download fails, the salary page
    // retries it itself (download=1) once the right period has loaded.
    const meta = parseActionUrl(notification?.actionUrl);
    if (notification?.type === "PAYROLL_PAID" && meta.year && meta.month) {
      setPayslipBusy(true);
      try {
        await downloadPayslipPdf({ year: Number(meta.year), month: Number(meta.month), language, profile });
      } catch (err) {
        console.error("Payslip download from notification failed:", err);
        if (path) path += `${path.includes("?") ? "&" : "?"}download=1`;
      } finally {
        setPayslipBusy(false);
      }
    }

    if (target) {
      // Synchronize the active view (storage + switcher event) BEFORE
      // navigating so the destination shell mounts in the right view and
      // the ViewTypeSwitcher never goes out of sync.
      applyViewSwitch(target.view);
      navigate(path);
    }
    onClose();
  };

  const handleLoadMore = (e) => {
    e.stopPropagation();
    if (pagination.hasMore && !loading) {
      fetchNotifications(pagination.page + 1, true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
  }, [isOpen, onClose]);

  // Escape closes the panel.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Skeletons only before the very first list arrives. Reopening shows the
  // in-memory list (kept live by the socket) instantly while a background
  // refresh runs — no loading flash on every open.
  const showSkeleton = !hasLoaded && !error;

  const tabs = [
    { key: "all", label: t.all, count: 0 },
    { key: "unread", label: t.unread, count: unreadCount },
  ];

  return (
    <div className="notification-backdrop" onClick={onClose}>
      <div
        className={`notification-dropdown ${isAr ? "rtl" : "ltr"}${isMobile ? " mobile" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={t.notifications}
      >
        <div className="notification-toolbar">
          <div className="notification-header">
            <h3 className="notification-title">{t.notifications}</h3>
            <div className="notification-header-actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  className="notification-icon-btn accent"
                  onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                  title={t.markAllRead}
                  aria-label={t.markAllRead}
                >
                  <CheckCheck size={16} aria-hidden="true" />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  type="button"
                  className="notification-icon-btn danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(t.confirmDeleteAll)) deleteAllNotifications();
                  }}
                  title={t.deleteAll}
                  aria-label={t.deleteAll}
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              )}
              {isMobile && (
                <button
                  type="button"
                  className="notification-icon-btn"
                  onClick={(e) => { e.stopPropagation(); onClose(); }}
                  title={t.close}
                  aria-label={t.close}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="notification-segment" role="tablist" aria-label={t.notifications}>
            {tabs.map((tab) => {
              const active = filter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`notification-tab${active ? " is-active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); setFilter(tab.key); }}
                >
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <span className="notification-tab-count">{tab.count > 99 ? "99+" : tab.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {payslipBusy && (
          <div
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              fontSize: "13px",
              fontWeight: 600,
              color: "#10b981",
              background: "rgba(16,185,129,0.1)",
              borderBottom: "1px solid rgba(16,185,129,0.25)",
            }}
          >
            <Loader2 size={16} className="animate-spin" />
            {t.downloadingPayslip}
          </div>
        )}

        <div className="notification-body">
          {showSkeleton ? (
            <div className="notification-skeleton" aria-busy="true" aria-label={t.loading}>
              {[0, 1, 2].map((i) => (
                <div key={i} className="notification-skeleton-row">
                  <span className="sk sk-avatar" />
                  <div className="sk-lines">
                    <span className="sk sk-line" style={{ width: "72%" }} />
                    <span className="sk sk-line sk-line-sm" style={{ width: "46%" }} />
                  </div>
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="notification-empty">
              <div className="notification-empty-icon">
                <BellOff size={22} aria-hidden="true" />
              </div>
              <p className="notification-empty-title">
                {filter === "unread" ? t.noUnread : t.noNotifications}
              </p>
              <p className="notification-empty-hint">
                {filter === "unread" ? t.noUnreadHint : t.emptyHint}
              </p>
            </div>
          ) : (
            <div className="notification-list">
              {groups.map((group) => (
                <section key={group.key} className="notification-group" aria-label={t[group.key]}>
                  <div className="notification-group-label">{t[group.key]}</div>
                  {group.items.map((notification) => (
                    <NotificationItem
                      // A live arrival/update re-keys the row so its arrival
                      // highlight plays once.
                      key={`${notification.id}:${notification._liveAt || 0}`}
                      notification={notification}
                      onClick={() => handleNotificationClick(notification)}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}

          {pagination.hasMore && notifications.length > 0 && !showSkeleton && (
            <button
              type="button"
              className="load-more-btn"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? <Loader2 size={16} className="spin" aria-hidden="true" /> : t.loadMore}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDropdown;
